/**
 * Activity Tab
 *
 * Full activity timeline component with filtering and date grouping.
 * Displays emails, calls, meetings, tasks, and notes for a record.
 */

import React, { useState } from 'react';
import { Clock, Plus, ChevronDown, Phone, Calendar, CheckSquare, FileText } from 'lucide-react';
import { useActivities, useAdvancedActivityFilters } from '../../hooks/useActivities';
import { ActivityTimeline } from '../activity/ActivityTimeline';
import { AdvancedActivityFilters } from '../activity/AdvancedActivityFilters';
import { Activity, RecordType } from '../../types/activity';

export type LogActivityType = 'call' | 'meeting' | 'task' | 'note';

interface ActivityTabProps {
  recordId?: string;
  recordType?: RecordType;
  currentUserId?: string;
  onLogActivity?: (type: LogActivityType) => void;
  onEditActivity?: (activity: Activity) => void;
  onDeleteActivity?: (activity: Activity) => void;
}

export const ActivityTab: React.FC<ActivityTabProps> = ({
  recordId,
  recordType,
  currentUserId,
  onLogActivity,
  onEditActivity,
  onDeleteActivity,
}) => {
  const [showLogMenu, setShowLogMenu] = useState(false);
  const queryFilters = recordId && recordType ? { recordId, recordType } : undefined;
  const { activities, loading, error } = useActivities(queryFilters, { enabled: !!queryFilters });
  const {
    filters,
    setTypeFilter,
    setUserIds,
    setSourceApps,
    setDateRange,
    clearFilters,
    hasActiveFilters,
    activeFilterCount,
    filteredActivities,
    availableUsers,
    availableSourceApps,
  } = useAdvancedActivityFilters(activities);

  const handleLogActivity = (type: LogActivityType) => {
    onLogActivity?.(type);
    setShowLogMenu(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500">{error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          Activity Timeline
        </h2>
        <div className="flex items-center gap-3">
          <AdvancedActivityFilters
            filters={filters}
            onTypeChange={setTypeFilter}
            onUserChange={setUserIds}
            onSourceAppChange={setSourceApps}
            onDateRangeChange={setDateRange}
            onClearFilters={clearFilters}
            availableUsers={availableUsers}
            availableSourceApps={availableSourceApps}
            hasActiveFilters={hasActiveFilters}
            activeFilterCount={activeFilterCount}
          />
          {onLogActivity && (
            <div className="relative">
              <button
                onClick={() => setShowLogMenu(!showLogMenu)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Log Activity
                <ChevronDown className="w-4 h-4" />
              </button>

              {showLogMenu && (
                <div className="absolute right-0 mt-1 w-40 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-10">
                  <button
                    onClick={() => handleLogActivity('call')}
                    className="w-full px-4 py-2 text-left text-sm text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 first:rounded-t-md"
                  >
                    <Phone className="w-4 h-4" /> Log Call
                  </button>
                  <button
                    onClick={() => handleLogActivity('meeting')}
                    className="w-full px-4 py-2 text-left text-sm text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                  >
                    <Calendar className="w-4 h-4" /> Log Meeting
                  </button>
                  <button
                    onClick={() => handleLogActivity('task')}
                    className="w-full px-4 py-2 text-left text-sm text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                  >
                    <CheckSquare className="w-4 h-4" /> Log Task
                  </button>
                  <button
                    onClick={() => handleLogActivity('note')}
                    className="w-full px-4 py-2 text-left text-sm text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 last:rounded-b-md"
                  >
                    <FileText className="w-4 h-4" /> Add Note
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Timeline or Empty State */}
      {filteredActivities.length > 0 ? (
        <ActivityTimeline
          activities={filteredActivities}
          currentUserId={currentUserId}
          onEdit={onEditActivity}
          onDelete={onDeleteActivity}
        />
      ) : (
        <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-12">
          <div className="text-center">
            <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No Activities Yet
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              {hasActiveFilters
                ? 'No activities match your current filters.'
                : 'Activities like emails, calls, and meetings will appear here.'}
            </p>
            {onLogActivity && (
              <button
                onClick={() => handleLogActivity('call')}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Log First Activity
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
